/**
 * Client-side route guard. Redirects to /login when the API reports no
 * authenticated session. Purely a UX gate — the API independently enforces
 * per-channel access on every protected call. Being signed in is enough to enter
 * the app; a vouched-in mod (not whitelisted) still has access to the channels
 * they were granted.
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "./useSession";
import { Skeleton, QueueSkeleton } from "../components/Skeleton";

export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();

  if (session.status === "loading") {
    // No app chrome is known yet (we haven't confirmed a session), so this
    // stands in for the whole shell — a bare topbar shape plus queue content,
    // matching what Layout renders once the session resolves.
    return (
      <div className="app">
        <div className="topbar" style={{ borderBottom: "none" }}>
          <Skeleton width={140} height={28} />
          <div className="spacer" />
          <Skeleton width={90} height={30} radius={6} />
        </div>
        <QueueSkeleton />
      </div>
    );
  }
  if (session.status === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
